import {
  UIBooker,
  UICart,
  UICartResponse,
  UIFlightData,
  UIOrderCreated,
  UIPassenger,
  UIProductSelected,
  UIProductSelection,
  UIProductSelectionWithType,
  UITrafficSource
} from "@flights/types";

import { clientFetch } from "@flights/client-fetch";
// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useActions, useStore } from "../store";
import { assert } from "../utils/assert";
import { useCallback } from "react";
import { actions as OrderActions } from "../store/order/actions";
import { actions as CartValidationErrorActions } from "../store/cartValidationError/actions";
import useGlobalContext from "./useGlobalContext";
import useContextName from "./useContextName";
import useCreateUrl from "./useCreateUrl";
import { getMarketingTrackingVariable } from "@flights/web-api-utils-universal";
import { actions as cartDetailsActions } from "../store/cartDetails/actions";
import useRewardDetailsData from "./useRewardDetailsData";
import useMultiProduct from "./useMultiProduct";
import { findFareIndex } from "../branded-fares/utils/fares";
import useEvents from "./useEvents";
import { actions as SanctionScreeningActions } from "../store/sanctionScreening/actions";
import useOfferCartOrderData from "./useOfferCartOrderData";
import { isCartValidationError } from "../utils/error-utils";

/**
 * # useCheckoutProcessor
 *
 * This manages finalizing the checkout process:
 *
 * - adding ancillaries to cart
 * - creating an order
 * - updating the store
 *
 * These steps have to be taken at the checkout step that precedes payment - since the checkout steps
 * are dynamic (ex. depending on seat map availability) this logic has to be extracted from any checkout screen component.
 *
 */

function useCheckoutProcessor() {
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const orderActions = useActions(OrderActions);
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const cartActions = useActions(cartDetailsActions);
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const cartValidationErrorActions = useActions(CartValidationErrorActions);
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const sanctionScreeningActions = useActions(SanctionScreeningActions);
  const { debugAllExtraProducts } = useGlobalContext();
  const contextName = useContextName();
  const { flight } = store.flightDetails;
  const { selected } = store.extraProducts;
  const { createUrl } = useCreateUrl();
  const { locale } = useGlobalContext();
  const { geniusLevel, airports } = useRewardDetailsData();
  const isMpShopEnabled = useMultiProduct();
  const { jsError } = useEvents();
  const offerCartOrderData = useOfferCartOrderData();

  /**
   * Creates a new cart
   * @return Promise<UICartResponse | undefined>
   */
  const createCart = useCallback(
    async (flight: UIFlightData) => {
      try {
        const cartResponse = await clientFetch<UICartResponse>("/api/cart/create", {
          method: "POST",
          body: JSON.stringify({
            offerToken: flight.token,
            offerPointOfSale: flight.pointOfSale || "",
            ...(flight.brandedFareOffers && {
              brandedFareIndex: findFareIndex(flight.token, flight.brandedFareOffers)
            })
          }),
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "X-Flights-Context-Name": contextName
          }
        });
        offerCartOrderData.set(flight?.token, { cart: cartResponse?.cart?.cartToken });
        cartActions.fetchSuccess(cartResponse);
        return cartResponse.cart;
      } catch (error) {
        cartActions.fetchError(error);
        jsError(error);
        throw error;
      }
    },
    [contextName, cartActions, jsError, offerCartOrderData]
  );

  /**
   * Update pax for cart
   * @return Promise<void>
   */
  const updatePax = useCallback(
    async (cartToken: string, passengers: UIPassenger[], booker: UIBooker) => {
      try {
        const cartResponse = await clientFetch<UICartResponse>(`/api/cart/${cartToken}/updatePax`, {
          method: "post",
          body: JSON.stringify({
            passengers,
            booker
          }),
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "X-Flights-Context-Name": contextName
          }
        });

        offerCartOrderData.set(cartToken, { cart: cartResponse?.cart?.cartToken });
        if (cartResponse?.cart?.reference) {
          cartActions.fetchSuccess(cartResponse);
          sanctionScreeningActions.reset();
          return cartResponse.cart;
        } else if (cartResponse.travellerSanctionScreening && cartResponse.travellerSanctionScreening.length > 0) {
          sanctionScreeningActions.set(cartResponse.travellerSanctionScreening);
          return cartResponse.cart;
        } else {
          const err = new Error("Missing cart data");
          jsError(err);
          cartActions.fetchError(err);
          throw err;
        }
      } catch (error) {
        jsError(error);
        cartActions.fetchError(error);
        throw error;
      }
    },
    [cartActions, contextName, jsError, sanctionScreeningActions, offerCartOrderData]
  );

  const recreateCart = useCallback(
    async (flight?: UIFlightData, passengers?: UIPassenger[], booker?: UIBooker) => {
      if (!flight) {
        throw new Error("Missing flight data");
      }

      let cart = await createCart(flight);

      const { cartToken } = cart;

      if (!cartToken) {
        throw new Error("Missing cart token");
      }

      if (!booker || !passengers) {
        throw new Error("Missing cart data to update pax");
      }

      cart = await updatePax(cartToken, passengers, booker);

      return cart;
    },
    [createCart, updatePax]
  );

  /**
   * Adds all ancillaries from store.extraProducts to cart
   * @return Promise
   */
  const addSelectedProductsToCart = useCallback(
    (
      cartToken?: string,
      options?: {
        doNotUpdateCartInStore?: boolean;
        offerRef?: string;
        addBaggage?: "only" | "skip";
        // ^^^ Since we can add only baggages to cart, we need a way to skip it
        // later on to avoid errors related to adding an existing ancillary.
      }
    ) => {
      const productsToAdd: UIProductSelectionWithType[] = [];

      assert(cartToken);

      Object.keys(selected).forEach((productType: keyof UIProductSelection) => {
        const products: UIProductSelected[] = selected[productType] || [];
        products.forEach((product) => {
          const isBaggages = ["cabinBaggage", "cabinBaggagePerTraveller", "specialBag", "checkedInBaggage"].includes(
            productType
          );
          if (options?.addBaggage === "skip" && isBaggages) return;
          if (options?.addBaggage === "only" && !isBaggages) return;
          productsToAdd.push({ type: productType, product });
        });
      });

      if (productsToAdd.length > 0) {
        cartActions.addProductFetch("loading");
        return clientFetch<any>(
          `/api/cart/${cartToken}/addProduct${options?.offerRef ? `?offerReference=${options?.offerRef}` : ""}`,
          {
            method: "POST",
            body: JSON.stringify(productsToAdd),
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              "X-Flights-Context-Name": contextName,
              ...(debugAllExtraProducts ? { "X-Flights-Debug-All-Products": "1" } : {})
            }
          }
        )
          .then((res) => {
            if (res.error) {
              throw res.error;
            }

            if (res && !options?.doNotUpdateCartInStore) {
              cartActions.fetchSuccess(res);
              cartActions.addProductFetch("success");
            }

            return Promise.resolve(res);
          })
          .catch((err) => {
            cartActions.addProductFetch("failed");
            throw err;
          });
      } else {
        return Promise.resolve();
      }
    },
    [selected, contextName, debugAllExtraProducts, cartActions]
  );

  /**
   * Create an order using the current cart reference
   * @return Promise
   */
  const createOrder = useCallback(
    (cart?: UICart, extraPayload?: {}) => {
      const flightToken = flight?.token;
      assert(cart);

      const { cartToken, reference: cartReference } = cart;

      assert(flightToken);
      assert(cartToken);
      assert(cartReference);

      const trafficSource: UITrafficSource = {
        // eslint-disable-next-line no-restricted-syntax -- this line was auto generated, hence fix the issue timely
        affiliateId: getMarketingTrackingVariable("aid") || null,
        // eslint-disable-next-line no-restricted-syntax -- this line was auto generated, hence fix the issue timely
        label: getMarketingTrackingVariable("label") || null,
        // eslint-disable-next-line no-restricted-syntax -- this line was auto generated, hence fix the issue timely
        adplat: getMarketingTrackingVariable("adplat") || null,
        // eslint-disable-next-line no-restricted-syntax -- this line was auto generated, hence fix the issue timely
        clickIdentifier: getMarketingTrackingVariable("ext-tr") || null
      };

      const orderData = {
        /*eslint-disable-next-line flights/no-unassigned-todo-comments*/
        // TODO: deprecated
        cartReference,
        cartToken,
        trafficSource,
        geniusLevel,
        airports,
        isMpShopEnabled,
        gclid: getMarketingTrackingVariable("gclid") || "",
        gbraid: getMarketingTrackingVariable("gbraid") || "",
        ...extraPayload
      };

      // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
      orderData["payment3DsRedirectUrl"] = decodeURI(
        // `<ORDER_TOKEN>` is a template replaced on java layer
        createUrl(`${window.location.origin}/checkout/payment/${flightToken}/<ORDER_TOKEN>`)
      );

      const headers = {
        "Content-Type": "application/json",
        "X-Flights-Context-Name": contextName
      };

      if (locale) {
        // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
        headers["X-Flights-Locale"] = locale;
      }

      return clientFetch<UIOrderCreated>("/api/order", {
        method: "post",
        body: JSON.stringify(orderData),
        credentials: "same-origin",
        headers
      })
        .then((orderCreated) => {
          const payload = { orderCreated };

          if (extraPayload && "priceChangeOrderId" in extraPayload) {
            // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
            payload["orderCreatedByPriceChange"] = true;
          }
          offerCartOrderData.set(cartToken, { order: payload?.orderCreated?.orderToken });
          orderActions.setOrderCreated(payload);
          return orderCreated;
        })
        .catch((err) => {
          if (isCartValidationError(err.code)) {
            cartValidationErrorActions.setErrorCode(err.code);
          }
          throw err;
        });
    },
    [
      flight,
      createUrl,
      orderActions,
      contextName,
      locale,
      geniusLevel,
      airports,
      isMpShopEnabled,
      offerCartOrderData,
      cartValidationErrorActions
    ]
  );

  return {
    recreateCart,
    createOrder,
    addSelectedProductsToCart
  };
}

export { useCheckoutProcessor };
